import React from 'react';

/*
 * Velaigal wordmark.
 *
 * Variants:
 *   mark     — just the geometric glyph (24x24 by default)
 *   wordmark — just the "Velaigal" text in Fraunces
 *   lockup   — mark + wordmark side-by-side (default, used in header/login)
 *   stacked  — mark centered above wordmark (for narrow/square contexts)
 *   tamil    — wordmark in Tamil script "வேலைகள்" using Noto Serif Tamil
 *
 * Glyph construction (per design spec §3.2): an abstract derivative of the
 * Tamil letter வே — a vertical ink stroke, an arc opening right (the kompu
 * curl), and a saffron pulli dot above. Strokes use currentColor so the mark
 * inherits the surrounding text color (ink in headers, cream in footers).
 * The saffron dot is always saffron — it is the brand accent.
 */

type Variant = 'mark' | 'wordmark' | 'lockup' | 'stacked' | 'tamil';
type Size = 'sm' | 'md' | 'lg' | 'hero';

interface WordmarkProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  /**
   * Override the saffron accent color of the dot (e.g. for monochrome
   * favicon rasterization). Defaults to the saffron token.
   */
  accentColor?: string;
  /**
   * Play a one-time pulse animation on the saffron pulli dot when the
   * component first mounts. Use on the landing hero, splash, or any
   * landmark moment where the brand should visibly enter. Defaults to off
   * so headers and footers don't pulse on every navigation.
   */
  animateOnMount?: boolean;
}

const TEXT_SIZE_PX: Record<Size, number> = {
  sm: 16,
  md: 22,
  lg: 28,
  hero: 80,
};

const MARK_SIZE_PX: Record<Size, number> = {
  sm: 18,
  md: 24,
  lg: 32,
  hero: 88,
};

function Mark({
  size,
  accentColor,
  animatePulse,
}: {
  size: number;
  accentColor: string;
  animatePulse: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <line
        x1="9"
        y1="7"
        x2="9"
        y2="21"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="square"
      />
      <path
        d="M 9 7 Q 17 7 17 14"
        stroke="currentColor"
        strokeWidth="1.75"
        fill="none"
        strokeLinecap="round"
      />
      <circle
        cx="9"
        cy="3.5"
        r="1.75"
        fill={accentColor}
        className={animatePulse ? 'velaigal-dot-enter' : undefined}
      />
    </svg>
  );
}

const Wordmark: React.FC<WordmarkProps> = ({
  variant = 'lockup',
  size = 'md',
  className = '',
  accentColor = '#C2410C',
  animateOnMount = false,
}) => {
  const textSize = TEXT_SIZE_PX[size];
  const markSize = MARK_SIZE_PX[size];

  const wordmarkText = (
    <span
      style={{
        fontFamily: '"Fraunces", Georgia, serif',
        fontSize: `${textSize}px`,
        fontWeight: 500,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
        color: 'currentColor',
      }}
    >
      Velaigal
    </span>
  );

  if (variant === 'mark') {
    return (
      <span
        className={className}
        aria-label="Velaigal"
        role="img"
        style={{ display: 'inline-flex', color: 'currentColor' }}
      >
        <Mark size={markSize} accentColor={accentColor} animatePulse={animateOnMount} />
      </span>
    );
  }

  if (variant === 'wordmark') {
    return (
      <span
        className={className}
        aria-label="Velaigal"
        style={{ display: 'inline-flex', alignItems: 'baseline', color: 'currentColor' }}
      >
        {wordmarkText}
      </span>
    );
  }

  if (variant === 'tamil') {
    return (
      <span
        className={className}
        aria-label="Velaigal"
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          fontFamily: '"Noto Serif Tamil", Georgia, serif',
          fontSize: `${Math.round(textSize * 1.15)}px`,
          fontWeight: 500,
          letterSpacing: '0',
          lineHeight: 1,
          color: 'currentColor',
        }}
      >
        வேலைகள்
      </span>
    );
  }

  if (variant === 'stacked') {
    return (
      <span
        className={className}
        aria-label="Velaigal"
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${Math.max(6, Math.round(markSize * 0.25))}px`,
          color: 'currentColor',
        }}
      >
        <Mark size={markSize} accentColor={accentColor} animatePulse={animateOnMount} />
        {wordmarkText}
      </span>
    );
  }

  // lockup (default)
  return (
    <span
      className={className}
      aria-label="Velaigal"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${Math.max(8, Math.round(markSize * 0.42))}px`,
        color: 'currentColor',
      }}
    >
      <Mark size={markSize} accentColor={accentColor} animatePulse={animateOnMount} />
      {wordmarkText}
    </span>
  );
};

export default Wordmark;
